import { getAxiosRequestConfig } from "app/ajax/service"
import { translate } from "app/language/service"
import {
  ALL_ICO_MIME_TYPES,
  ALL_IMAGE_MIME_TYPES,
  ALL_VIDEO_MIME_TYPES,
  CARRIER_RETURN,
  ICsvFileColumn,
  ICSVRow,
  IFileUploadOnProgressFn
} from "app/model"
import axios, { AxiosRequestConfig } from "axios"
import jschardet from "jschardet"

export function downloadCsvFile(fileNameWithoutExtension: string, csvFileContent: string): void {
  // link is created for download purpose only, it's never diplayed
  const link = document.createElement("a")
  link.href = "data:text/csv;charset=utf-8," + encodeURI(csvFileContent)
  link.target = "_blank"
  link.download = `${fileNameWithoutExtension}.csv`
  link.click()
}

export function uploadFile(url: string, file: File, onProgress?: IFileUploadOnProgressFn): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    const formData = new FormData()
    formData.set("file", file)
    const config: AxiosRequestConfig = getAxiosRequestConfig()
    if (onProgress) {
      config.onUploadProgress = (progressEvent: any) =>
        onProgress({ loadedBytes: progressEvent.loaded, totalBytes: progressEvent.total })
    }
    axios
      .post(url, formData, config)
      .then(response => resolve(response))
      .catch(error => {
        console.error(error)
        reject("An error occured while uploading the file")
      })
  })
}

// @recursive
export function readCsvFile(files: FileList, charset?: string): Promise<ICSVRow[]> {
  return new Promise<ICSVRow[]>((resolve, reject) => {
    const selectedFile = files[0]
    const reader = new FileReader()
    reader.onload = async function (this: FileReader, event: ProgressEvent<FileReader>): Promise<any> {
      if (!event.target) {
        return reject(Error(translate("file.not.readable")))
      }
      if (typeof event.target.result !== "string") {
        return reject(Error(translate("file.not.readable")))
      }
      try {
        const fileCharset = jschardet.detect(event.target.result).encoding
        if (fileCharset !== "UTF-8" && fileCharset !== charset) {
          // @recursivity
          resolve(await readCsvFile(files, fileCharset))
        } else {
          resolve(parseCSVContent(event.target.result, ","))
        }
      } catch (error) {
        reject(error)
      }
    }
    reader.readAsText(selectedFile, charset)
  })
}

export function generateCsvFileContent(sortedColumns: ICsvFileColumn[], rows: any[], separator: string = ","): string {
  let csvFileContent: string = `${sortedColumns.map(c => c.label).join(",")}${CARRIER_RETURN}`
  for (const row of rows) {
    for (let i = 0; i < sortedColumns.length; i++) {
      const sortedColumn = sortedColumns[i]
      csvFileContent += `${getStringCsvValue(row[sortedColumn.key], separator)}${
        i === sortedColumns.length - 1 ? CARRIER_RETURN : separator
      }`
    }
  }
  return csvFileContent
}

export function getFormattedFileSize(sizeInByte: number, precision: number = 2): string {
  const fileSize = sizeInByte.toString()
  if (fileSize.length < 7) {
    return `${(sizeInByte / 1024).toFixed(precision)}KB`
  }
  return `${(sizeInByte / 1048576).toFixed(precision)}MB` // 1048576 = 1024 * 1024
}

export const isImageFile = (mimeType: string | null): boolean => ALL_IMAGE_MIME_TYPES.includes(mimeType as string)

export const isIcoFile = (mimeType: string | null): boolean => ALL_ICO_MIME_TYPES.includes(mimeType as string)

export function isVideoFile(mimeType: string | null): boolean {
  return ALL_VIDEO_MIME_TYPES.includes(mimeType as string)
}

function parseCSVContent(content: string, delimiter: string): ICSVRow[] {
  content = content.replace(/\r/g, "\n").replace(/\n\n/g, "\n")
  const firstCarrierReturnIndex = content.indexOf("\n")
  const columns = content.slice(0, firstCarrierReturnIndex).split(delimiter)
  const rawRows = content.slice(firstCarrierReturnIndex + 1).split("\n")
  if (rawRows.length === 1) {
    throw Error("file.csv.error.only.one.row")
  }
  const rows = []
  for (const rawRow of rawRows) {
    if (!rawRow || rawRow.trim() === "") {
      continue
    }
    const rowValues = rawRow.split(delimiter)
    const row: ICSVRow = columns.reduce((currentRow, columnName, columnIndex) => {
      const columnValue = rowValues[columnIndex]
      if (columnValue === undefined) {
        throw Error(translate("file.csv.error.row.bad.format", columnIndex))
      }
      currentRow[columnName] = columnValue.trim()
      return currentRow
    }, {} as ICSVRow)
    rows.push(row)
  }
  return rows
}

function getStringCsvValue(value: any, separator: string): string {
  if (value === null || value === undefined) {
    return ""
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  return String(value).replace(new RegExp(separator, "g"), " ")
}
